import { addYears } from 'date-fns/addYears';
import { startOfDay } from 'date-fns/startOfDay';
import { toDate } from 'date-fns/toDate';
import observer from 'saddlebag-observer';

import customSet from '../../../utils/dateHandling/customSet';
import {
  SELECT_DAY,
  SELECT_MONTH,
  SELECT_WHOLE_YEAR,
  SET_IS_RETURN,
  SET_HAS_HOTEL_DATA_ON_BOTH_DATES,
  SET_TRIP_DURATION,
  CLEAR_TRIP_DURATION,
} from '../helpers/constants/actionsConstants';
import * as constants from '../helpers/constants/dataTypeConstants';
import validators from '../helpers/dateSelectionValidators';

import type {
  DateSelectionAction,
  SelectDayAction,
  SelectMonthAction,
} from '../actions/types';
import type { DateSelectionState } from '../selectors/types';

const today = () => startOfDay(new Date());

const initialState: DateSelectionState = {
  minOutboundDate: today(),
  minInboundDate: today(),
  inboundMonthDate: null,
  outboundMonthDate: today(),
  minDate: today(),
  maxDate: addYears(today(), 1),
  selectedOutbound: today(),
  selectedInbound: null,
  wholeYear: false,
  isReturn: false,
  hasHotelDataOnBothDates: true,
  tripDuration: null,
  cheapestCombinations: null,
};

const validateSelectDayArgs = (action: SelectDayAction) => {
  const { dayOfMonth } = action.payload;
  if (
    !Number.isInteger(dayOfMonth) ||
    dayOfMonth < constants.MIN_MONTH_DAY ||
    dayOfMonth > constants.MAX_MONTH_DAY
  ) {
    throw new Error(`Invalid dayOfMonth provided: ${dayOfMonth}`);
  }
};

const validateSelectMonthArgs = (action: SelectMonthAction) => {
  const { month } = action.payload;
  if (
    !Number.isInteger(month) ||
    month < constants.MIN_MONTH ||
    month > constants.MAX_MONTH
  ) {
    throw new Error(`Invalid month provided: ${month}`);
  }
};

const formatDay = (date: Date) => `0${date.getDate()}`.slice(-2);

const yearMonth = (date: Date) => {
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  return `${year.toString().slice(-2)}${`0${month.toString()}`.slice(-2)}`;
};

const reduceSelectDay = (
  state: DateSelectionState,
  action: SelectDayAction,
): DateSelectionState => {
  validateSelectDayArgs(action);
  const { dayOfMonth, direction } = action.payload;

  if (direction === constants.OUTBOUND_DIRECTION) {
    const newOutbound = toDate(state.selectedOutbound);
    newOutbound.setDate(dayOfMonth);
    observer.publish('update-outbound-day', formatDay(newOutbound));

    const nextState: DateSelectionState = {
      ...state,
      selectedOutbound: newOutbound,
      minInboundDate: newOutbound,
      wholeYear: false,
    };

    if (state.selectedInbound && newOutbound > state.selectedInbound) {
      observer.publish('update-inbound-day', formatDay(newOutbound));
      return {
        ...nextState,
        selectedInbound: newOutbound,
      };
    }

    return nextState;
  }

  if (direction === constants.INBOUND_DIRECTION && state.isReturn) {
    if (!state.selectedInbound) {
      return state;
    }
    const newInbound = toDate(state.selectedInbound);
    newInbound.setDate(dayOfMonth);
    observer.publish('update-inbound-day', formatDay(newInbound));
    return {
      ...state,
      selectedInbound: newInbound,
      wholeYear: false,
    };
  }

  return state;
};

const reduceSelectMonth = (
  state: DateSelectionState,
  action: SelectMonthAction,
): DateSelectionState => {
  validateSelectMonthArgs(action);
  const { direction, month, year } = action.payload;
  const newMonthDate = customSet(year, month - 1, 1);

  if (direction === constants.OUTBOUND_DIRECTION) {
    const newDates = validators.getValidDatesOnOutboundMonthChange(
      newMonthDate,
      state.inboundMonthDate,
    );
    observer.publish(
      'update-outbound-year-month',
      yearMonth(newDates.selectedOutbound),
    );
    if (newDates.inboundMonthDate) {
      observer.publish(
        'update-inbound-year-month',
        yearMonth(newDates.inboundMonthDate),
      );
    }
    return { ...state, ...newDates, wholeYear: false };
  }

  if (direction === constants.INBOUND_DIRECTION) {
    const {
      inboundMonthDate,
      minInboundDate,
      selectedInbound,
      ...outboundUpdates
    } = validators.getValidDatesOnInboundMonthChange(
      state.outboundMonthDate,
      newMonthDate,
    );

    const newDates = state.isReturn
      ? {
          inboundMonthDate,
          selectedInbound,
          minInboundDate,
          ...outboundUpdates,
        }
      : {
          minInboundDate,
          ...outboundUpdates,
        };

    // union type so TS doesn't know if inboundMonthDate exists
    if ('inboundMonthDate' in newDates && newDates.inboundMonthDate) {
      observer.publish(
        'update-inbound-year-month',
        yearMonth(newDates.inboundMonthDate),
      );
    }
    if (newDates.outboundMonthDate) {
      observer.publish(
        'update-outbound-year-month',
        yearMonth(newDates.outboundMonthDate),
      );
    }

    return { ...state, ...newDates, wholeYear: false };
  }

  return state;
};

const reducer = (
  state: DateSelectionState = initialState,
  action: DateSelectionAction,
): DateSelectionState => {
  switch (action.type) {
    case SELECT_DAY:
      return reduceSelectDay(state, action);

    case SELECT_MONTH:
      return reduceSelectMonth(state, action);

    case SELECT_WHOLE_YEAR:
      return {
        ...state,
        wholeYear: action.payload,
      };

    case SET_IS_RETURN:
      return {
        ...state,
        isReturn: action.payload,
      };

    case SET_HAS_HOTEL_DATA_ON_BOTH_DATES:
      return {
        ...state,
        hasHotelDataOnBothDates: action.payload,
      };

    case SET_TRIP_DURATION:
      return {
        ...state,
        tripDuration: action.payload.tripDuration,
      };

    case CLEAR_TRIP_DURATION:
      return {
        ...state,
        tripDuration: null,
      };

    case 'SET_CHEAPEST_COMBINATIONS':
      return {
        ...state,
        cheapestCombinations: action.payload,
      };

    default:
      return state;
  }
};

export default reducer;
